import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { Lock, Sparkles, ExternalLink } from "lucide-react";
import { db } from "../authentication/firebase-config";

/**
 * MembershipGate
 *
 * Wraps the authenticated app surface. Reads `users/{uid}/membership` from
 * Firestore and decides whether to render the dashboard, the paywall card,
 * or pass through (legacy mode).
 *
 * Membership doc shape:
 *   {
 *     active: boolean,
 *     tier:   "galaxy" | "apex",
 *     source: "manual-audit" | "ghl-webhook" | ...,
 *     ghlContactId?: string,
 *     expiresAt?:    Timestamp,
 *     updatedAt:     Timestamp
 *   }
 *
 * Decision matrix:
 *
 *   ┌────────────────────────────┬──────────────────────────────────┐
 *   │ Membership doc state       │ What we render                   │
 *   ├────────────────────────────┼──────────────────────────────────┤
 *   │ active: true               │ children (dashboard)             │
 *   │ active: false              │ paywall card                     │
 *   │ doc missing                │ depends on LEGACY_MODE_PASSTHROUGH│
 *   │ Firestore unreachable      │ children (fail-open, log warning)│
 *   └────────────────────────────┴──────────────────────────────────┘
 *
 * `LEGACY_MODE_PASSTHROUGH` is the rollout switch:
 *
 *   - true  (Phase A, tonight):  no doc = treat as legacy = pass through.
 *           This is the safe default while we still have ~355 existing
 *           users we haven't audited yet. Nobody gets locked out tonight.
 *
 *   - false (Phase B, after audit): no doc = treat as not-a-member =
 *           show paywall. Flipped only after we've written membership
 *           docs for confirmed paying users.
 *
 * To flip it: change the LEGACY_MODE_PASSTHROUGH constant, ship a deploy.
 * A future iteration could put this behind a feature flag in Firestore
 * so it's flippable without a redeploy.
 */

const LEGACY_MODE_PASSTHROUGH = true; // Phase A — flip to false after audit
const MEMBERSHIP_PURCHASE_URL = "https://arcrypto.io/membership";

/* ─── Membership reader ─────────────────────────────────────────── */

async function fetchMembership(uid) {
  if (!uid) return { state: "missing", data: null };
  try {
    const snap = await getDoc(doc(db, "users", uid, "settings", "membership"));
    if (!snap.exists()) return { state: "missing", data: null };
    return { state: "ok", data: snap.data() };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[MembershipGate] read failed, failing open:", err.message);
    return { state: "error", data: null };
  }
}

/* ─── Paywall card ──────────────────────────────────────────────── */

const PaywallCard = ({ tierHint }) => (
  <div className="min-h-screen flex items-center justify-center p-6 bg-arc-bg">
    <div className="max-w-md w-full surface-xl p-7 sm:p-8 text-center">
      <div className="mx-auto mb-5 w-14 h-14 rounded-2xl bg-arc-gold/15 border border-arc-gold/30 flex items-center justify-center">
        <Lock size={22} className="text-arc-gold" />
      </div>

      <p className="eyebrow text-arc-gold mb-2">Membership required</p>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-arc-ink mb-3">
        FIRE Tracker is an ARCrypto member benefit
      </h1>
      <p className="text-sm text-arc-muted leading-relaxed mb-6">
        Your account doesn't currently have an active Galaxy or Apex
        membership. Renew or activate your membership to regain access.
      </p>

      <a
        href={MEMBERSHIP_PURCHASE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-tactile btn-gold w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold mb-3"
      >
        <Sparkles size={14} />
        View membership options
        <ExternalLink size={12} />
      </a>

      <p className="text-[11px] text-arc-dim mt-4">
        Already a member and seeing this in error? Contact support and we'll
        get your access restored.
      </p>

      {tierHint && (
        <p className="text-[10px] text-arc-dim mt-2 font-mono uppercase tracking-[0.14em]">
          last tier: {tierHint}
        </p>
      )}
    </div>
  </div>
);

/* ─── Gate component ────────────────────────────────────────────── */

const MembershipGate = ({ user, children }) => {
  const [decision, setDecision] = useState({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    if (!user || !user.uid) {
      // No user — the gate is downstream of auth, so this shouldn't
      // happen, but render children to avoid trapping anyone.
      setDecision({ status: "pass" });
      return;
    }

    fetchMembership(user.uid).then(({ state, data }) => {
      if (cancelled) return;

      if (state === "ok" && data && data.active === true) {
        setDecision({ status: "pass", data });
        return;
      }

      if (state === "ok" && data && data.active === false) {
        setDecision({ status: "blocked", data });
        return;
      }

      if (state === "missing") {
        // Either legacy user (Phase A) or unprovisioned new user (Phase B).
        setDecision({
          status: LEGACY_MODE_PASSTHROUGH ? "pass" : "blocked",
          data: null,
        });
        return;
      }

      // state === "error" → fail open
      setDecision({ status: "pass", data: null });
    });

    return () => {
      cancelled = true;
    };
  }, [user]);

  if (decision.status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-arc-bg">
        <div className="w-8 h-8 rounded-full border-2 border-arc-gold/30 border-t-arc-gold animate-spin" />
      </div>
    );
  }

  if (decision.status === "blocked") {
    return <PaywallCard tierHint={decision.data?.tier} />;
  }

  return children;
};

export default MembershipGate;
